/**
 * Stage2_Audio - 角色与音频阶段
 * 
 * 用户旅程第三步：生成角色形象和语音朗读
 * 路由: /create/stage/2?storyId=xxx
 * 
 * 【修复】添加了浏览器自动播放策略处理，确保用户点击后能正常播放
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { StepIndicator } from '../../components/create/StepIndicator';
import { useAudioContext } from '../../hooks/useAudioContext';
import { useCreationStore } from '../../store/useCreationStore';
import {
  Loader2,
  Play,
  Pause,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  User,
  Volume2,
  Image as ImageIcon,
  VolumeX,
  X,
  Gauge,
  Check,
  AlertTriangle,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface StoryData {
  id: string;
  title: string;
  stage: string;
  pageCount: number;
  textContent?: {
    pages: Array<{
      pageNumber: number;
      text: string;
    }>;
  };
  characterData?: {
    name: string;
    imageUrl: string;
    description: string;
  };
  audioUrls?: string[];
  photoUrl?: string;
  config?: {
    speed?: number;
  };
  audioPreference?: {
    speed?: number;
  };
  creationMode?: string;
}

export default function Stage2_Audio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('storyId');
  
  const [story, setStory] = useState<StoryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [currentPlaying, setCurrentPlaying] = useState<number | null>(null);
  const [regeneratingPage, setRegeneratingPage] = useState<number | null>(null);
  
  // 音频播放错误提示
  const [showAudioHint, setShowAudioHint] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  // 换音色弹窗
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isChangingVoice, setIsChangingVoice] = useState(false);
  const [pendingVoice, setPendingVoice] = useState<string | null>(null);
  const [pendingSpeed, setPendingSpeed] = useState<number>(0.9);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // 音频缓存破坏参数：重新生成后强制浏览器拉取最新音频
  const [audioBustMap, setAudioBustMap] = useState<Record<number, number>>({});
  // 音频同步状态
  const [audioSync, setAudioSync] = useState<{
    pages: Array<{
      pageNumber: number;
      hasAudio: boolean;
      textUpdatedAt: string | null;
      audioGeneratedAt: string | null;
      isStale: boolean;
    }>;
    staleCount: number;
    stalePageNumbers: number[];
    allSynced: boolean;
  } | null>(null);
  const [, setIsCheckingSync] = useState(false);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  // 语速范围
  const SPEED_MIN = 0.7;
  const SPEED_MAX = 1.3;
  const SPEED_STEP = 0.05;

  const voiceOptions = [
    { id: 'xiaoyan', label: '小燕', desc: '温柔女声' },
    { id: 'xiaoyu', label: '小宇', desc: '活泼男声' },
    { id: 'xiaomei', label: '小美', desc: '可爱童声' },
    { id: 'xiaogang', label: '小刚', desc: '稳重男声' },
  ];

  // MiniMax 原始 voice_id → 前端别名 的反向映射
  const minimaxToFrontendMap: Record<string, string> = {
    'female-shaonv': 'xiaoyan',
    'male-qn-qingse': 'xiaoyu',
    'lovely_girl': 'xiaomei',
    'male-qn-jingying': 'xiaogang',
  };
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isSubmittingRef = useRef(false);
  const isRegeneratingRef = useRef(false);
  const isChangingVoiceRef = useRef(false);
  const { isUnlocked, unlock, state } = useAudioContext();
  const { visual } = useCreationStore();

  useEffect(() => {
    if (!storyId) {
      navigate('/create');
      return;
    }
    fetchStory();
    
    // 组件卸载时停止音频
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [storyId]);

  const fetchStory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}`);
      const result = await response.json();
      const fetchedStory = result.data;
      setStory(fetchedStory);
      // 同步当前音色为已生成的音色（将 MiniMax 原始 ID 反向映射为前端别名）
      const generatedVoice = (fetchedStory?.audioPreference as any)?.voiceId;
      if (generatedVoice) {
        const frontendVoiceId = minimaxToFrontendMap[generatedVoice] || generatedVoice;
        if (frontendVoiceId !== visual.voice) {
          useCreationStore.getState().setVisual({ voice: frontendVoiceId });
        }
      }
      // 同步语速到 store
      const fetchedSpeed = (fetchedStory?.audioPreference as any)?.speed;
      if (fetchedSpeed !== undefined && fetchedSpeed !== useCreationStore.getState().story.speed) {
        useCreationStore.getState().setStory({ speed: fetchedSpeed });
      }
    } catch (error) {
      console.error('获取故事失败:', error);
      showNotification('error', '获取故事失败');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAudioSync = async () => {
    if (!storyId) return;
    setIsCheckingSync(true);
    try {
      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/audio-sync`);
      const result = await res.json();
      if (result.success) {
        setAudioSync(result.data);
      }
    } catch (e) {
      console.error('获取音频同步状态失败:', e);
    } finally {
      setIsCheckingSync(false);
    }
  };

  useEffect(() => {
    if (!storyId) return;
    fetchAudioSync();
  }, [storyId]);

  // 生成角色和全部音频
  const generateAll = async () => {
    if (isSubmittingRef.current) {
      console.log('[Stage2] Double click blocked by ref lock');
      return;
    }
    isSubmittingRef.current = true;

    // 如果已有音频，说明是全部重生成，先预检费用并二次确认
    const hasAudioBefore = (story?.audioUrls || []).filter(Boolean).length > 0;
    if (hasAudioBefore) {
      try {
        const costRes = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/regenerate-all-cost`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        const costData = await costRes.json();
        if (costData.success) {
          const cost = costData.data.cost || 0;
          const freeCount = costData.data.freeCount || 0;
          const message = cost > 0
            ? `⚠️ 全部重新生成将覆盖已有音频\n\n将扣除 ¥${cost.toFixed(2)}，确认继续？`
            : `⚠️ 全部重新生成将覆盖已有音频\n\n本次在免费额度内（剩余 ${freeCount} 次），但仍会覆盖现有内容。\n\n确认继续？`;
          const confirmed = window.confirm(message);
          if (!confirmed) return;
        }
      } catch (e) {
        console.error('费用预检失败', e);
      }
    }

    setIsGeneratingAll(true);
    try {
      // 获取语速设置（优先使用用户自定义，否则根据年龄推荐）
      const customSpeed = story?.audioPreference?.speed || story?.config?.speed;
      // 获取音色设置
      const voice = visual.voice;
      
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customSpeed, voice }),
      });
      const result = await response.json();
      
      if (result.success) {
        showNotification('success', '角色和音频生成成功！');
        await fetchStory();
        await fetchAudioSync();
      } else if (result.needRecharge) {
        showNotification('error', '余额不足，请充值后再试');
      } else {
        showNotification('error', '生成失败: ' + result.error);
      }
    } catch (error) {
      console.error('生成失败:', error);
      showNotification('error', '生成失败');
    } finally {
      isSubmittingRef.current = false;
      setIsGeneratingAll(false);
    }
  };

  // 重录单段音频
  const regenerateAudio = async (pageNumber: number) => {
    if (isRegeneratingRef.current) {
      console.log('[Stage2] Regenerate double click blocked by ref lock');
      return;
    }
    isRegeneratingRef.current = true;

    // 预检费用
    try {
      const costRes = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/regenerate-cost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const costData = await costRes.json();
      if (costData.success) {
        const confirmMsg = costData.data.cost > 0
          ? `本次重录将扣除 ¥${costData.data.cost.toFixed(2)}，确认继续？`
          : `本次为第 ${costData.data.usedCount + 1} 次免费重录（共 ${costData.data.freeCount} 次），确认重新生成？`;
        if (!window.confirm(confirmMsg)) return;
      }
    } catch (e) {
      console.error('费用预检失败', e);
    }

    setRegeneratingPage(pageNumber);
    try {
      // 获取音色设置
      const voice = visual.voice;
      
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageNumber, voice }),
      });
      const result = await response.json();
      
      if (result.success) {
        showNotification('success', `第${pageNumber}页音频已重新生成`);
        // 设置缓存破坏参数，确保下次试听立即拉取最新音频
        setAudioBustMap(prev => ({ ...prev, [pageNumber]: Date.now() }));
        await fetchStory();
        await fetchAudioSync();
      } else if (result.needRecharge) {
        alert('余额不足，请充值后再试');
      } else {
        showNotification('error', '重录失败: ' + result.error);
      }
    } catch (error) {
      console.error('重录失败:', error);
      showNotification('error', '重录失败');
    } finally {
      isRegeneratingRef.current = false;
      setRegeneratingPage(null);
    }
  };

  // 换音色重生成
  const changeVoice = async (newVoiceId: string, newSpeed: number) => {
    if (isChangingVoiceRef.current) {
      console.log('[Stage2] Voice change double click blocked by ref lock');
      return;
    }
    isChangingVoiceRef.current = true;

    const isSpeedOnly = newVoiceId === visual.voice;
    const isActuallyVoiceChange = newVoiceId !== visual.voice;

    // 预检费用
    try {
      if (isActuallyVoiceChange) {
        const costRes = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/voice-change-cost`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        const costData = await costRes.json();
        if (costData.success && costData.data.cost > 0) {
          const confirmed = window.confirm(`换音色将扣除 ¥${costData.data.cost.toFixed(2)}，确认继续？`);
          if (!confirmed) return;
        }
      } else if (isSpeedOnly) {
        const costRes = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/regenerate-all-cost`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        const costData = await costRes.json();
        if (costData.success && costData.data.cost > 0) {
          const confirmed = window.confirm(`调整语速将扣除 ¥${costData.data.cost.toFixed(2)}，确认继续？`);
          if (!confirmed) return;
        }
      }
    } catch (e) {
      console.error('费用预检失败', e);
    }

    setIsChangingVoice(true);
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          customSpeed: newSpeed, 
          voice: newVoiceId, 
          isVoiceChange: isActuallyVoiceChange,
          isSpeedOnly: isSpeedOnly 
        }),
      });
      const result = await response.json();
      if (result.success) {
        const label = voiceOptions.find(v => v.id === newVoiceId)?.label || newVoiceId;
        showNotification('success', `更换完成，音色已更换为${label}，语速${newSpeed.toFixed(2)}x`);
        // 同步更新本地 store
        useCreationStore.getState().setVisual({ voice: newVoiceId });
        useCreationStore.getState().setStory({ speed: newSpeed });
        await fetchStory();
        await fetchAudioSync();
        setShowVoiceModal(false);
        setPendingVoice(null);
      } else if (result.needRecharge) {
        alert('余额不足，请充值后再试');
      } else {
        showNotification('error', '换音色失败: ' + result.error);
      }
    } catch (error) {
      console.error('换音色失败:', error);
      showNotification('error', '换音色失败');
    } finally {
      isChangingVoiceRef.current = false;
      setIsChangingVoice(false);
    }
  };

  // 【关键】构建音频URL
  const buildAudioUrl = (audioUrl: string): string => {
    // 如果已经是完整URL（如COS外链），直接返回
    if (audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) {
      return audioUrl;
    }
    
    // 本地相对路径，确保以/开头
    const normalizedPath = audioUrl.startsWith('/') ? audioUrl : `/${audioUrl}`;
    return normalizedPath;
  };

  // 构建图片URL（处理相对路径）
  const buildImageUrl = (imageUrl: string): string => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    const normalizedPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return normalizedPath;
  };

  // 播放/暂停音频 - 【关键修复】处理浏览器自动播放策略
  const togglePlay = async (pageNumber: number, audioUrl: string) => {
    console.log(`[Audio] 点击播放: 第${pageNumber}页`);
    console.log(`[Audio] 原始URL: ${audioUrl}`);
    console.log(`[Audio] AudioContext状态: ${state}, 已解锁: ${isUnlocked}`);
    
    if (currentPlaying === pageNumber) {
      // 暂停当前播放
      audioRef.current?.pause();
      setCurrentPlaying(null);
      return;
    }

    // 停止之前的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // 【关键】首次播放时解锁音频上下文
    if (!isUnlocked) {
      console.log('[Audio] 首次播放，解锁音频上下文...');
      await unlock();
    }

    // 构建完整URL（附加缓存破坏参数，避免浏览器使用旧缓存）
    let fullUrl = buildAudioUrl(audioUrl);
    const bust = audioBustMap[pageNumber];
    if (bust) {
      fullUrl += fullUrl.includes('?') ? `&t=${bust}` : `?t=${bust}`;
    }

    // 创建并播放音频
    const audio = new Audio(fullUrl);
    audioRef.current = audio;
    
    // 【关键】详细错误处理
    audio.onended = () => {
      console.log('[Audio] 播放结束');
      setCurrentPlaying(null);
    };
    
    audio.onerror = (e) => {
      const errorCode = audio.error?.code;
      const errorMessage = audio.error?.message;
      console.error('[Audio] 加载错误:', {
        code: errorCode,
        message: errorMessage,
        url: fullUrl,
        event: e
      });
      
      let errorMsg = '音频加载失败';
      switch (errorCode) {
        case 1: errorMsg = '音频加载被中止'; break;
        case 2: errorMsg = '网络错误：无法下载音频'; break;
        case 3: errorMsg = '音频解码错误：文件可能损坏'; break;
        case 4: errorMsg = '不支持的音频格式'; break;
      }
      
      setAudioError(`${errorMsg} (URL: ${fullUrl.substring(0, 50)}...)`);
      setCurrentPlaying(null);
    };
    
    // 添加更多事件监听用于调试
    audio.oncanplay = () => console.log('[Audio] 可以播放');
    audio.oncanplaythrough = () => console.log('[Audio] 可以流畅播放');
    audio.onwaiting = () => console.log('[Audio] 等待数据');
    audio.onstalled = () => console.log('[Audio] 下载停滞');

    try {
      // 【关键】确保音频上下文已恢复后再播放
      if (state === 'suspended') {
        await unlock();
      }
      
      console.log('[Audio] 开始播放...');
      await audio.play();
      setCurrentPlaying(pageNumber);
      setAudioError(null);
      console.log('[Audio] 播放成功');
    } catch (error: any) {
      console.error('[Audio] 播放失败:', error);
      
      // 处理浏览器自动播放阻止
      if (error.name === 'NotAllowedError') {
        setShowAudioHint(true);
        setAudioError('浏览器阻止了自动播放，请点击页面任意位置开启音频');
        
        // 3秒后自动隐藏提示
        setTimeout(() => setShowAudioHint(false), 5000);
      } else {
        setAudioError('播放失败: ' + error.message);
      }
      
      setCurrentPlaying(null);
    }
  };

  // 处理页面点击解锁音频（备用方案）
  const handlePageClick = async () => {
    if (!isUnlocked && state === 'suspended') {
      console.log('[Audio] 页面点击，尝试解锁音频...');
      await unlock();
      setShowAudioHint(false);
    }
  };

  // 返回上一阶段（修改文案）
  const goToPrevStage = () => {
    const hasAudioBefore = (story?.audioUrls || []).filter(Boolean).length > 0;
    if (hasAudioBefore) {
      const confirmed = window.confirm(
        '⚠️ 返回修改文案后，当前已生成的音频内容可能与新文案不匹配。\n\n' +
        '如需重新生成音频，请回到本阶段点击"全部重新生成"。\n\n' +
        '确定要返回修改文案吗？'
      );
      if (!confirmed) return;
    }
    if (story?.creationMode === 'DIY_MANUAL') {
      navigate(`/create/diy/editor/${storyId}`);
    } else {
      navigate(`/create/stage/1?storyId=${storyId}&flow=ai`);
    }
  };

  // 进入下一阶段
  const goToNextStage = async () => {
    try {
      await fetch(`${API_BASE_URL}/stories/${storyId}/stage/2/next`, {
        method: 'POST',
      });
      navigate(`/create/stage/3?storyId=${storyId}`);
    } catch (error) {
      console.error('进入下一阶段失败:', error);
      showNotification('error', '进入下一阶段失败');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const hasGenerated = story?.characterData && story?.audioUrls && story.audioUrls.length > 0;
  const currentStep = 2;
  const pages = story?.textContent?.pages || [];

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8"
      onClick={handlePageClick}
    >
      <div className="w-full flex justify-center">
        <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col gap-6">
        {/* Toast 通知 */}
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-sm font-medium ${
              notification.type === 'success'
                ? 'bg-emerald-500 text-white'
                : 'bg-rose-500 text-white'
            }`}
          >
            {notification.type === 'success' ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
            {notification.message}
          </motion.div>
        )}

        {/* 音频解锁提示 */}
        <AnimatePresence>
          {showAudioHint && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="fixed top-4 left-1/2 -translate-x-1/2 z-50"
            >
              <div className="bg-amber-500 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2">
                <VolumeX className="w-5 h-5" />
                <span>🔇 请点击页面任意位置开启音频预览</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 错误提示 */}
        <AnimatePresence>
          {audioError && !showAudioHint && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="fixed top-4 left-1/2 -translate-x-1/2 z-50"
            >
              <div className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg">
                {audioError}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 音频同步警告 */}
        {audioSync && !audioSync.allSynced && audioSync.staleCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 flex-1">
              <p className="font-medium">⚠️ 音频与文案不同步</p>
              <p className="text-red-700 mt-0.5">
                第 {audioSync.stalePageNumbers.join(', ')} 页的文案在音频生成后被修改过，当前音频可能不准确。
                建议点击"重新生成过期音频"进行同步。
              </p>
            </div>
          </motion.div>
        )}

        {/* 上游内容提示 */}
        {(story?.audioUrls || []).filter(Boolean).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">当前音频已生成</p>
              <p className="text-amber-700 mt-0.5">如需修改文案，请点击"上一步"返回 Stage1。修改后请回到本阶段点击"全部重新生成"以同步音频内容。</p>
            </div>
          </motion.div>
        )}

        {/* 步骤导航 */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 w-full">
          <StepIndicator currentStep={currentStep} />
        </motion.div>

        {/* 页面标题 */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
            {hasGenerated ? '预览角色与配音' : '创作角色与配音'}
          </h1>
          <p className="text-gray-600">
            {hasGenerated 
              ? 'AI已生成角色形象和语音朗读，你可以预览效果'
              : `点击下方按钮生成角色形象和${story?.pageCount || 12}页音频朗读`}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧：角色形象 */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="h-full bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-500" />
                  <h2 className="text-lg font-semibold">绘本角色</h2>
                </div>
                
                {hasGenerated ? (
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <div className="w-40 h-40 sm:w-48 md:w-56 sm:h-48 md:h-56 rounded-xl overflow-hidden bg-slate-100">
                        {story?.characterData?.imageUrl ? (
                          <img
                            src={buildImageUrl(story.characterData.imageUrl)}
                            alt={story.characterData.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              const parent = (e.target as HTMLImageElement).parentElement;
                              if (parent) {
                                const fallback = document.createElement('div');
                                fallback.className = 'w-full h-full flex items-center justify-center';
                                fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-300"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                                parent.appendChild(fallback);
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-16 h-16 text-gray-300" />
                          </div>
                        )}
                      </div>
                    </div>
                    <h3 className="font-medium text-gray-900">{story?.characterData?.name}</h3>
                    <p className="text-sm text-slate-500 mt-4">{story?.characterData?.description}</p>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400 flex flex-col items-center gap-2">
                    <ImageIcon className="w-16 h-16" />
                    <p>AI将为您生成绘本角色形象</p>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>

          {/* 右侧：音频列表 */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-2">
            <Card className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-5 h-5 text-blue-500" />
                    <h2 className="text-lg font-semibold">配音预览</h2>
                    {hasGenerated && (
                      <span className="text-sm text-gray-500">
                        {story?.audioUrls?.length || 0} / {pages.length} 页
                      </span>
                    )}
                  </div>
                </div>

                {hasGenerated ? (
                  <div className="max-h-[500px] overflow-y-auto flex flex-col gap-4">
                    {pages.map((page, index) => {
                      const audioUrl = story?.audioUrls?.[index];
                      const isPlaying = currentPlaying === page.pageNumber;
                      
                      const isStale = audioSync?.pages?.find(p => p.pageNumber === page.pageNumber)?.isStale;

                      return (
                        <div
                          key={page.pageNumber}
                          className={`flex items-center gap-2 rounded-lg border p-3 transition-colors ${
                            isPlaying
                              ? 'bg-indigo-50 border-indigo-200'
                              : isStale
                              ? 'bg-red-50 border-red-200 hover:border-red-300'
                              : 'bg-white border-slate-100 hover:border-slate-200'
                          }`}
                        >
                          <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-medium ${
                            isStale ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {page.pageNumber}
                          </span>
                          {isStale && (
                            <span className="text-xs text-red-500 font-medium hidden sm:inline">文案已改</span>
                          )}
                          
                          <p className="flex-1 text-sm text-gray-700 leading-relaxed">
                            {page.text}
                          </p>
                          
                          <div className="flex items-center gap-2">
                            {audioUrl ? (
                              <>
                                <button
                                  onClick={() => togglePlay(page.pageNumber, audioUrl)}
                                  className={`rounded-xl p-2 transition-colors ${
                                    isPlaying
                                      ? 'bg-indigo-600 text-white'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                  title={isPlaying ? '暂停' : '播放'}
                                >
                                  {isPlaying ? (
                                    <Pause className="w-4 h-4" />
                                  ) : (
                                    <Play className="w-4 h-4" />
                                  )}
                                </button>
                                
                                <button
                                  onClick={() => regenerateAudio(page.pageNumber)}
                                  disabled={regeneratingPage === page.pageNumber}
                                  className="rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50 p-2"
                                  title="重新生成"
                                >
                                  {regeneratingPage === page.pageNumber ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4" />
                                  )}
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">未生成</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <Volume2 className="w-16 h-16 mx-auto mb-4" />
                    <p>点击上方按钮生成音频</p>
                    <p className="text-sm mt-2">AI将为{story?.pageCount || 12}页故事生成配音</p>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        </div>

        {/* 底部操作按钮：两行布局，移动端友好 */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-2 pt-2"
        >
          {/* 第一行：上一步 */}
          <div className="flex justify-start">
            <Button
              variant="secondary"
              onClick={goToPrevStage}
              className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 whitespace-nowrap shrink-0"
            >
              <ChevronLeft className="w-4 h-4 shrink-0" />
              上一步
            </Button>
          </div>

          {/* 第二行：操作按钮 */}
          {hasGenerated ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="secondary"
                onClick={generateAll}
                isLoading={isGeneratingAll}
                disabled={isGeneratingAll}
                leftIcon={<RefreshCw className="w-4 h-4 shrink-0" />}
                className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 whitespace-nowrap shrink-0"
              >
                全部重新生成
              </Button>

              {audioSync && !audioSync.allSynced && audioSync.staleCount > 0 && (
                <Button
                  variant="secondary"
                  onClick={generateAll}
                  isLoading={isGeneratingAll}
                  disabled={isGeneratingAll}
                  leftIcon={<RefreshCw className="w-4 h-4 shrink-0 text-red-500" />}
                  className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl hover:bg-red-100 whitespace-nowrap shrink-0"
                >
                  重新生成过期音频 ({audioSync.staleCount}页)
                </Button>
              )}

              <Button
                variant="secondary"
                onClick={() => {
                  setPendingVoice(visual.voice);
                  setPendingSpeed(story?.audioPreference?.speed || story?.config?.speed || useCreationStore.getState().story.speed || 0.9);
                  setShowVoiceModal(true);
                }}
                disabled={isChangingVoice}
                className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 whitespace-nowrap shrink-0"
              >
                <Volume2 className="w-4 h-4 shrink-0" />
                换音色
              </Button>

              <Button
                onClick={goToNextStage}
                className="flex items-center gap-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 whitespace-nowrap shrink-0"
              >
                <CheckCircle className="w-4 h-4 shrink-0" />
                下一步
                <ChevronRight className="w-4 h-4 shrink-0" />
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button
                onClick={generateAll}
                isLoading={isGeneratingAll}
                disabled={isGeneratingAll}
                leftIcon={<Volume2 className="w-4 h-4 shrink-0" />}
                className="flex items-center gap-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 whitespace-nowrap shrink-0"
              >
                生成角色与配音
              </Button>
            </div>
          )}
        </motion.div>

        {/* 换音色弹窗 */}
        <AnimatePresence>
          {showVoiceModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => !isChangingVoice && setShowVoiceModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">更换音色</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      当前：
                      <span className="font-medium text-slate-700">
                        {voiceOptions.find(v => v.id === visual.voice)?.label || visual.voice}（{voiceOptions.find(v => v.id === visual.voice)?.desc || ''}）· 语速 {(story?.audioPreference?.speed || story?.config?.speed || 0.9).toFixed(2)}x
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => !isChangingVoice && setShowVoiceModal(false)}
                    disabled={isChangingVoice}
                    className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* 音色选择 */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {voiceOptions.map((voice) => {
                    const isCurrent = voice.id === visual.voice;
                    const isSelected = voice.id === pendingVoice;
                    return (
                      <button
                        key={voice.id}
                        onClick={() => setPendingVoice(voice.id)}
                        disabled={isChangingVoice}
                        className={`
                          relative rounded-xl border-2 transition-all text-left p-3
                          ${isSelected
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : isCurrent
                            ? 'border-amber-300 bg-amber-50/50 text-slate-700'
                            : 'border-slate-200 hover:border-indigo-200'
                          }
                          disabled:opacity-50
                        `}
                      >
                        {isCurrent && (
                          <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-400 text-white text-[10px] font-bold rounded-full">
                            当前
                          </span>
                        )}
                        <div className="font-medium">{voice.label}</div>
                        <div className={`text-xs ${isSelected ? 'text-indigo-600/70' : 'text-slate-500'}`}>{voice.desc}</div>
                      </button>
                    );
                  })}
                </div>

                {/* 语速调节 */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <Gauge className="w-4 h-4 text-indigo-500" />
                      语速
                    </label>
                    <span className={`text-sm font-semibold ${
                      pendingSpeed < 0.9 ? 'text-blue-600' : pendingSpeed > 1.1 ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {pendingSpeed.toFixed(2)}x
                      {pendingSpeed < 0.9 && ' (慢速)'}
                      {pendingSpeed > 1.1 && ' (快速)'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={SPEED_MIN}
                    max={SPEED_MAX}
                    step={SPEED_STEP}
                    value={pendingSpeed}
                    onChange={(e) => setPendingSpeed(parseFloat(e.target.value))}
                    disabled={isChangingVoice}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500 disabled:opacity-50"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>慢速 (适合初学)</span>
                    <span>正常</span>
                    <span>快速 (适合进阶)</span>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setShowVoiceModal(false)}
                    disabled={isChangingVoice}
                    className="text-slate-700"
                  >
                    取消
                  </Button>
                  <Button
                    onClick={() => pendingVoice && changeVoice(pendingVoice, pendingSpeed)}
                    disabled={isChangingVoice || !pendingVoice || (pendingVoice === visual.voice && Math.abs(pendingSpeed - (story?.audioPreference?.speed || story?.config?.speed || 0.9)) < 0.001)}
                    className="flex items-center gap-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isChangingVoice ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        更换中...
                      </>
                    ) : (
                      '确认更换音色'
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  </div>
  );
}
